import React, { useContext, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment-timezone';
import { Panel } from 'primereact/panel';
import { FormattedMessage, defineMessages, useIntl } from 'react-intl';
import gql from 'graphql-tag';
import { Formik } from 'formik';
import * as Yup from 'yup';
import apollo from '../../services/apollo';
import { growlErrors } from '../../utils/NotificationUtil';
import { searchJobProfile } from '../../services/apps/JobProfileService';
import { getObjectFromQueryParam, stringify } from '../../utils/UrlUtil';
import DataExportForm from './DataExportForm';
import { validationMessages } from '../../i18n/common';
import { useLocation, useHistory } from 'react-router-dom';
import { AccountContext } from '../../components/AccountContext';
import { NotificationContext } from '../../components/GrowlNotifications';
import { useSelector } from 'react-redux';
import useSubscriptionExpiredRestrictions from '../../hooks/useSubscriptionExpiredRestrictions';

const EXPORT_REPORT = gql`
  mutation($input: DataExportInput!) {
    generateDataExportReport(input: $input) {
      url
    }
  }
`;

const messages = defineMessages({
  selectDefaultText: {
    id: 'actions.select',
    defaultMessage: '- Select -',
  },
  withTargetJobOptionText: {
    id: 'export.respondent.withTargetJob',
    defaultMessage: 'Respondents with Job Profile as Target Job',
  },
  allTargetJobOptionText: {
    id: 'export.respondent.all',
    defaultMessage: 'All respondents possible',
  },
  placeholderStartDate: {
    id: 'placeholder.startWith',
    defaultMessage: 'Start date',
  },
  placeholderEndDate: {
    id: 'placeholder.endWith',
    defaultMessage: 'End date',
  },
  generate: { id: 'actions.generate', defaultMessage: 'Generate' },
});

const DataExport = ({ templates, accountHierarchy }) => {
  const history = useHistory();
  const location = useLocation();
  const { search } = useLocation();
  const { formatMessage } = useIntl();
  const { locale } = useSelector((state) => state.i18n);

  const currentAccount = useContext(AccountContext);
  const growl = useContext(NotificationContext);
  const { subscriptionIsExpired } = useSubscriptionExpiredRestrictions();

  const queryObject = getObjectFromQueryParam(search);
  const queryParamsDefaultState = {
    startDate: queryObject ? queryObject.startDate : null,
    endDate: queryObject ? queryObject.endDate : null,
    locale: queryObject ? queryObject.locale : locale,
    respondentType: queryObject ? queryObject.respondentType : null,
    assessmentTemplate: queryObject ? queryObject.assessmentTemplate : null,
    jobProfile: queryObject ? queryObject.jobProfile : null,
    accountIds: queryObject ? queryObject.accountIds || [] : [],
  };

  const [isJpLoading, setIsJsLoading] = useState(false);
  const [jobProfiles, setJobProfiles] = useState([]);
  const [queryParams, setQueryParams] = useState(queryParamsDefaultState);
  const [assessmentTemplate, setAssessmentTemplate] = useState(queryParams ? queryParams.assessmentTemplate : null);

  const searchJob = async (query = null, tempId = null) => {
    try {
      setIsJsLoading(true);
      setJobProfiles([]);

      const data = await searchJobProfile({
        aid: currentAccount,
        tempId,
        query,
      });

      setJobProfiles(data);
    } catch (err) {
      growlErrors(growl, err);
    } finally {
      setIsJsLoading(false);
    }
  };

  useEffect(() => {
    searchJob(assessmentTemplate);
    // eslint-disable-next-line
  }, []);

  const updateQueryParams = (newQueryParams) => {
    const mixedParams = { ...queryParams, ...newQueryParams };
    const search = stringify({
      ...mixedParams,
    });

    setQueryParams(mixedParams);

    // update url with the new search, keep the rest of the values
    history.push({
      ...location,
      search,
    });
  };

  const changeURLParams = (field, value) => {
    const newParams = { [field]: value };
    updateQueryParams(newParams);
  };

  const changeURLDateParams = (field, value) => {
    const date = new Date(Date.UTC(value.getFullYear(), value.getMonth(), value.getDate()));

    const dateValue = date ? moment(date).format('YYYY-MM-DD') : null;

    const newParams = { [field]: dateValue };
    updateQueryParams(newParams);
  };

  const changeAsmtTemplate = (value) => {
    setAssessmentTemplate({ assessmentTemplate: value });
    searchJob(null, value);
    const newParams = { assessmentTemplate: value, jobProfile: null };
    updateQueryParams(newParams);
  };

  const exportPageData = async (formValues) => {
    const {
      respondentType,
      jobProfile,
      locale,
      onlyScored,
      withInterestsScores,
      withAriFields,
      accountIds,
    } = formValues;

    const template = formValues.assessmentTemplate;

    const startDate = new Date(
      Date.UTC(formValues.startDate.getFullYear(), formValues.startDate.getMonth(), formValues.startDate.getDate()),
    );
    const endDate = new Date(
      Date.UTC(formValues.endDate.getFullYear(), formValues.endDate.getMonth(), formValues.endDate.getDate()),
    );

    const from = startDate ? moment(startDate).format('YYYY-MM-DD') : null;
    const to = endDate ? moment(endDate).format('YYYY-MM-DD') : null;

    const inputValues = {
      template,
      jobProfile,
      filter: respondentType,
      locale,
      from,
      to,
      account: currentAccount,
      accountIds,
      onlyScored,
      withInterestsScores,
      withAriFields,
    };
    try {
      const result = await apollo.mutate({
        mutation: EXPORT_REPORT,
        variables: {
          input: inputValues,
        },
      });
      window.open(result.data.generateDataExportReport.url, '_blank');
    } catch (errors) {
      growlErrors(growl, errors);
    }
  };
  const templatesForDropDown = templates.map((template) => ({
    value: template.id,
    label: template.title,
  }));
  const templateList = [{ value: '', label: formatMessage(messages.selectDefaultText) }, ...templatesForDropDown];

  const accountsForDropdown = accountHierarchy.map((account) => ({
    value: account.descendantId,
    label: account.descendantBrandDisplayName,
  }));

  const initialValues = {
    locale,
    startDate: queryParams.startDate ? new Date(queryParams.startDate) : null,
    endDate: queryParams.endDate ? new Date(queryParams.endDate) : null,
    assessmentTemplate: queryParams.assessmentTemplate,
    jobProfile: queryParams.jobProfile,
    respondentType: queryParams.respondentType,
    onlyScored: subscriptionIsExpired ? true : false,
    accountIds: queryParams.accountIds,
  };

  const validationSchema = Yup.object().shape({
    assessmentTemplate: Yup.string().required(formatMessage(validationMessages.required)),
    jobProfile: Yup.string()
      .required(formatMessage(validationMessages.required))
      .nullable(),
    respondentType: Yup.string().required(formatMessage(validationMessages.required)),
    locale: Yup.string().required(formatMessage(validationMessages.required)),
  });

  return (
    <div className="p-grid">
      <div className="p-col-12">
        <div className="brand-detailed-wrapper">
          <div className="p-sm-12 p-md-6">
            <Panel
              header={
                <span>
                  <FormattedMessage id="export.title" defaultMessage="Data Export" />
                  -
                  <FormattedMessage id="export.overview" defaultMessage="Export data to file" />
                </span>
              }
            >
              <Formik
                enableReinitialize
                initialValues={initialValues}
                validationSchema={validationSchema}
                onSubmit={async (value, { setSubmitting }) => {
                  await exportPageData(value);
                  setSubmitting(false);
                }}
              >
                <DataExportForm
                  templates={templateList}
                  accounts={accountsForDropdown}
                  jobSearching={isJpLoading}
                  jobs={jobProfiles}
                  searchJob={searchJob}
                  changeURLParams={changeURLParams}
                  changeURLDateParams={changeURLDateParams}
                  changeAsmtTemplate={changeAsmtTemplate}
                />
              </Formik>
            </Panel>
          </div>
        </div>
      </div>
    </div>
  );
};

DataExport.propTypes = {
  templates: PropTypes.arrayOf(PropTypes.any).isRequired,
};

export default DataExport;
